var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo, useRef, useState } from "react";
import { nodesApi } from "@api/enpoints/nodes-api";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
function rowHasEmailOrPhone(c) {
    var _a, _b;
    const email = (_a = c.email) === null || _a === void 0 ? void 0 : _a.trim();
    const raw = (_b = c.number) === null || _b === void 0 ? void 0 : _b.trim();
    const phoneOk = !!raw && raw !== "+31 ";
    return !!(email || phoneOk);
}
function phoneForApi(raw) {
    const t = raw === null || raw === void 0 ? void 0 : raw.trim();
    if (!t || t === "+31 ")
        return undefined;
    return t.replace(/\s/g, "");
}
/**
 * Debounced server checks so AP and linked node alarm lists do not share the same email/phone (duplicate alerts).
 */
export function useAlarmRecipientCrossValidation(opts) {
    const { t } = useTranslation("common");
    const { target, nodeUid, accessPointUid, contacts, debounceMs = 450, onBlockedChange, } = opts;
    const [saveBlocked, setSaveBlocked] = useState(false);
    const [validating, setValidating] = useState(false);
    const prevBlocked = useRef(false);
    const lastMessage = useRef(null);
    const contactsKey = useMemo(() => JSON.stringify((contacts !== null && contacts !== void 0 ? contacts : []).map((c) => {
        var _a, _b, _c, _d;
        return [
            (_b = (_a = c.email) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "",
            (_d = (_c = c.number) === null || _c === void 0 ? void 0 : _c.trim()) !== null && _d !== void 0 ? _d : "",
        ];
    })), [contacts]);
    useEffect(() => {
        onBlockedChange === null || onBlockedChange === void 0 ? void 0 : onBlockedChange(saveBlocked);
    }, [saveBlocked, onBlockedChange]);
    useEffect(() => {
        const ac = new AbortController();
        const timer = window.setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const list = contacts !== null && contacts !== void 0 ? contacts : [];
            const rowsToCheck = list
                .map((c, index) => ({ c, index }))
                .filter(({ c }) => rowHasEmailOrPhone(c));
            if (rowsToCheck.length === 0) {
                setSaveBlocked(false);
                setValidating(false);
                prevBlocked.current = false;
                return;
            }
            if (target === "node" && !(nodeUid === null || nodeUid === void 0 ? void 0 : nodeUid.trim())) {
                setSaveBlocked(false);
                setValidating(false);
                return;
            }
            if (target === "accessPoint" && !(accessPointUid === null || accessPointUid === void 0 ? void 0 : accessPointUid.trim())) {
                setSaveBlocked(false);
                setValidating(false);
                return;
            }
            setValidating(true);
            let anyBlocked = false;
            let firstMessage = null;
            try {
                for (const { c } of rowsToCheck) {
                    const email = ((_a = c.email) === null || _a === void 0 ? void 0 : _a.trim()) || undefined;
                    const phone = phoneForApi(c.number);
                    if (!email && !phone)
                        continue;
                    const params = {
                        target: target === "node" ? "node" : "accessPoint",
                    };
                    if (target === "node")
                        params.nodeUid = nodeUid.trim();
                    if (target === "accessPoint")
                        params.accessPointUid = accessPointUid.trim();
                    if (email)
                        params.email = email;
                    if (phone)
                        params.phone = phone;
                    const res = yield nodesApi.validateAlarmRecipientContact(params, ac.signal);
                    const payload = res.data;
                    const data = payload === null || payload === void 0 ? void 0 : payload.data;
                    if (ac.signal.aborted)
                        return;
                    if (!(payload === null || payload === void 0 ? void 0 : payload.isSuccess) || data == null) {
                        anyBlocked = false;
                        firstMessage = null;
                        break;
                    }
                    if (data.isAllowed === false) {
                        anyBlocked = true;
                        firstMessage = (_b = firstMessage !== null && firstMessage !== void 0 ? firstMessage : payload.message) !== null && _b !== void 0 ? _b : null;
                        break;
                    }
                }
                if (ac.signal.aborted)
                    return;
                setSaveBlocked(anyBlocked);
                if (anyBlocked && !prevBlocked.current) {
                    const msg = firstMessage !== null && firstMessage !== void 0 ? firstMessage : t("toasts.conflictingAlarmContact");
                    if (lastMessage.current !== msg) {
                        toast.error(msg);
                        lastMessage.current = msg;
                    }
                }
                if (!anyBlocked)
                    lastMessage.current = null;
                prevBlocked.current = anyBlocked;
            }
            catch (_c) {
                if (!ac.signal.aborted) {
                    setSaveBlocked(false);
                    prevBlocked.current = false;
                }
            }
            finally {
                if (!ac.signal.aborted)
                    setValidating(false);
            }
        }), debounceMs);
        return () => {
            clearTimeout(timer);
            ac.abort();
        };
    }, [target, nodeUid, accessPointUid, contactsKey, debounceMs]);
    return { saveBlocked, validating };
}
